import {format} from 'date-fns'
import {excludeFormatList} from '@/variables'
import {formatLargeNumber, formatString as formatStringUtil, formatValueWithCurrency} from '../utils'

const formatString = (value: string): string => formatStringUtil(value)

const formatStoreName = (value: string): string => {
  if (!value) {
    return ''
  }
  const width = typeof window !== 'undefined' ? window.innerWidth : 1920
  if (width >= 1600) {
    return value
  }
  if (width < 1600 && width >= 1400) {
    return value.substring(0, 17)
  }
  if (width < 1400 && width >= 1000) {
    return value.substring(0, 15)
  }
  if (width < 1000) {
    return value.substring(0, 10)
  }
  return value
}

const formatStringNoUpperCase = (value: string | boolean): string => {
  if (typeof value === 'boolean') {
    value = value ? 'yes' : 'no'
  }
  return (value as string).replace(/_/g, ' ')
}

const capitalize = (value: string): string => {
  if (!value) {
    return ''
  }
  const str = value.toString()
  return str.charAt(0).toUpperCase() + str.slice(1)
}

const twoDecimals = (value?: string | number, header?: string): string | number => {
  if (value === undefined || value === null || value === '') {
    return '0.00'
  }
  if (header && excludeFormatList.includes(header)) {
    return value
  }
  return formatNumber(value, 2)
}

const toFixedZero = (value?: string | number, header?: string): string | number => {
  if (value === undefined || value === null || value === '') {
    return '0'
  }
  if ((header && excludeFormatList.includes(header)) || typeof value === 'string') {
    return value
  }
  if (isNaN(Number(value))) {
    return value
  }
  return formatNumber(value, 0)
}

const formatThousands = (value?: string | number, header?: string): string => {
  if (value === undefined || value === null || value === '') {
    return '0'
  }

  if ((header && excludeFormatList.includes(header)) || typeof value === 'string') {
    return value.toString()
  }
  return formatNumber(value, 0)
}

const formatDate = (value: string | number | Date, pattern: string): string => {
  return format(new Date(value), pattern)
}

const addSign = (value: number | string): string => {
  const num = parseInt(String(value))
  return (num >= 0 ? '+' : '') + value
}

export const formatWeekNumber = (value: string | number): string => {
  const valueStr = String(value)
  if (valueStr.length < 4) {
    return ''
  }
  const year = valueStr.substring(0, 4)
  const week = valueStr.substring(4, 6)
  return `${year} - W${week}`
}

export const formatPercents = (value: string | number): number => {
  const num = Number(value)
  if (Math.abs(num) === Infinity) {
    return 0
  }
  return Math.round(num * 100)
}

const roundToStep = (value: number, step: number): number => {
  return Math.round(value / step) * step
}

const currency_no_decimals = (value: number): string => {
  if (Math.abs(value) >= 1_000_000) {
    return formatPrice(value, 0) // formatPrice handles millions via formatValueWithCurrency
  }
  return formatPrice(value, 0)
}

const currencyWithDecimals = (value: number): string => {
  return formatPrice(value, 2)
}

const percentage = (value: number | null): string => {
  if (value === null) {
    return ''
  }
  return value + '%'
}

const integer = (value: number | null): string => {
  if (value === null) {
    return ''
  }
  return formatNumber(value, 0)
}

export const formatPrice = (value: number | string, decimals = 2, currency = undefined): string => {
  if (value === undefined || value === null || value === '') {
    return ''
  }
  return formatValueWithCurrency(Number(value), decimals, currency).toString()
}

export const formatNumber = (value: number | string, decimals = 0, minDecimals?: number): string => {
  if (value === undefined || value === null || value === '') {
    return ''
  }
  if (minDecimals === undefined) {
    minDecimals = decimals
  }
  return Number(value).toLocaleString('en-GB', {
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: decimals
  })
}

export const filters = {
  formatStoreName,
  formatString,
  formatStringNoUpperCase,
  capitalize,
  // We should keep this function name, for dynamic filter usage
  currency: formatPrice,
  twoDecimals,
  toFixedZero,
  formatThousands,
  formatDate,
  addSign,
  formatWeekNumber,
  formatPercents,
  roundToStep,
  currency_no_decimals,
  currencyWithDecimals,
  percentage,
  integer,
  formatPrice,
  formatNumber,
  formatLargeNumber
}
